import { useEffect, useRef, useState, type FormEvent } from 'react';
import { supabase } from '@/lib/supabase';
import { uploadSupportAttachment } from '@/lib/supportAttachments';

interface SupportRequest {
  id: string;
  request_code: string;
  name: string;
  subject: string;
  message: string;
  status: string;
  created_at: string;
  reply_count: number;
  last_reply_at: string | null;
}

interface Reply {
  id: string;
  content: string;
  is_staff: boolean;
  created_at: string;
  attachment_url: string | null;
  attachment_name: string | null;
}

const statusMeta: Record<string, { label: string; cls: string }> = {
  open: { label: 'Mới', cls: 'bg-accent-500/15 text-accent-400' },
  answered: { label: 'Đã trả lời', cls: 'bg-emerald-500/15 text-emerald-400' },
  resolved: { label: 'Đã giải quyết', cls: 'bg-secondary-500/15 text-secondary-400' },
};

const formatDate = (iso: string | null) => {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleString('vi-VN', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const isImageAttachment = (reply: Reply) => {
  const ref = (reply.attachment_name || reply.attachment_url || '').toLowerCase();
  return /\.(png|jpe?g|gif|webp)(\?|$)/i.test(ref);
};

export default function Mailbox({ onLogout }: { onLogout: () => void }) {
  const [requests, setRequests] = useState<SupportRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [replies, setReplies] = useState<Reply[]>([]);
  const [replyLoading, setReplyLoading] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);
  const [attachment, setAttachment] = useState<File | null>(null);
  const [attachmentPreview, setAttachmentPreview] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const selectedCodeRef = useRef<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const [editingReplyId, setEditingReplyId] = useState<string | null>(null);
  const [editReplyText, setEditReplyText] = useState('');
  const [savingReply, setSavingReply] = useState(false);
  const [editingMessage, setEditingMessage] = useState(false);
  const [editMessageText, setEditMessageText] = useState('');
  const [savingMessage, setSavingMessage] = useState(false);

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const { data, error: rpcError } = await supabase.rpc('list_support_requests');
      if (rpcError) throw rpcError;
      setRequests((data as SupportRequest[]) || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Không thể tải danh sách yêu cầu.');
    } finally {
      setLoading(false);
    }
  };

  const selected = requests.find((r) => r.id === selectedId) || null;

  const loadReplies = async (req: SupportRequest) => {
    setReplyLoading(true);
    try {
      const { data, error: rpcError } = await supabase.rpc('get_support_request', { p_code: req.request_code });
      if (rpcError) throw rpcError;
      const payload = data as { replies?: Reply[] } | null;
      setReplies((payload?.replies as Reply[]) || []);
    } catch {
      setReplies([]);
    } finally {
      setReplyLoading(false);
    }
  };

  const refreshRepliesByCode = async (code: string) => {
    try {
      const { data, error: rpcError } = await supabase.rpc('get_support_request', { p_code: code });
      if (rpcError) return;
      const payload = data as { replies?: Reply[] } | null;
      setReplies((payload?.replies as Reply[]) || []);
    } catch {
      /* ignore */
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selected) {
      selectedCodeRef.current = selected.request_code;
      loadReplies(selected);
    } else {
      selectedCodeRef.current = null;
      setReplies([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId]);

  useEffect(() => {
    const channel = supabase
      .channel('support-replies-rt')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'support_replies' }, () => {
        load();
        const code = selectedCodeRef.current;
        if (code) refreshRepliesByCode(code);
      })
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-scroll messages to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [replies, replyLoading]);

  const handleFileChange = (file: File | null) => {
    if (!file) return;
    setAttachment(file);
    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = () => setAttachmentPreview(reader.result as string);
      reader.readAsDataURL(file);
    } else {
      setAttachmentPreview(null);
    }
  };

  const clearAttachment = () => {
    setAttachment(null);
    setAttachmentPreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleReply = async (e: FormEvent) => {
    e.preventDefault();
    if (!selected || (!replyText.trim() && !attachment)) return;
    setSending(true);
    setUploading(!!attachment);
    try {
      let attachmentUrl: string | null = null;
      let attachmentName: string | null = null;
      if (attachment) {
        const uploaded = await uploadSupportAttachment(attachment, selected.id);
        attachmentUrl = uploaded.url;
        attachmentName = uploaded.name;
      }
      const { error: rpcError } = await supabase.rpc('reply_support_request', {
        p_request_id: selected.id,
        p_content: replyText.trim(),
        p_attachment_url: attachmentUrl,
        p_attachment_name: attachmentName,
      });
      if (rpcError) throw rpcError;
      setReplyText('');
      clearAttachment();
      await loadReplies(selected);
      load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Không thể gửi trả lời.');
    } finally {
      setSending(false);
      setUploading(false);
    }
  };

  const handleStatusChange = async (newStatus: string) => {
    if (!selected) return;
    try {
      const { error: rpcError } = await supabase.rpc('update_support_request_status', {
        p_request_id: selected.id,
        p_status: newStatus,
      });
      if (rpcError) throw rpcError;
      load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Không thể cập nhật trạng thái.');
    }
  };

  const startEditReply = (reply: Reply) => {
    setEditingReplyId(reply.id);
    setEditReplyText(reply.content);
  };

  const saveReply = async () => {
    if (!editingReplyId) return;
    setSavingReply(true);
    try {
      const { error: rpcError } = await supabase.rpc('update_support_reply', {
        p_reply_id: editingReplyId,
        p_content: editReplyText.trim(),
      });
      if (rpcError) throw rpcError;
      setEditingReplyId(null);
      if (selected) await loadReplies(selected);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Không thể cập nhật.');
    } finally {
      setSavingReply(false);
    }
  };

  const startEditMessage = () => {
    if (!selected) return;
    setEditingMessage(true);
    setEditMessageText(selected.message);
  };

  const saveMessage = async () => {
    if (!selected) return;
    setSavingMessage(true);
    try {
      const { error: rpcError } = await supabase.rpc('update_support_request_message', {
        p_request_id: selected.id,
        p_message: editMessageText.trim(),
      });
      if (rpcError) throw rpcError;
      setEditingMessage(false);
      load();
      if (selected) {
        const s = { ...selected, message: editMessageText.trim() };
        setRequests((prev) => prev.map((r) => (r.id === s.id ? s : r)));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Không thể cập nhật.');
    } finally {
      setSavingMessage(false);
    }
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
        <div>
          <h1 className="text-xl font-bold text-foreground-950">Hộp thư hỗ trợ</h1>
          <p className="text-foreground-600 text-xs mt-0.5">Tổng {requests.length} yêu cầu</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={load} className="px-3 py-1.5 rounded-md border border-background-300/60 text-xs font-semibold text-foreground-600 hover:text-primary-400 whitespace-nowrap cursor-pointer transition-colors">
            <i className="ri-refresh-line mr-1"></i> Tải lại
          </button>
          <button onClick={onLogout} className="px-3 py-1.5 rounded-md border border-background-300/60 text-xs font-semibold text-foreground-600 hover:text-rose-400 whitespace-nowrap cursor-pointer transition-colors">
            <i className="ri-logout-box-r-line mr-1"></i> Đăng xuất
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-4 rounded-lg bg-rose-500/15 border border-rose-500/30 p-3 flex items-center gap-2">
          <i className="ri-error-warning-line text-rose-400 text-lg"></i>
          <p className="text-sm text-rose-400">{error}</p>
        </div>
      )}

      {loading ? (
        <div className="py-16 text-center text-sm text-foreground-600">Đang tải...</div>
      ) : requests.length === 0 ? (
        <div className="rounded-xl border border-background-200/60 bg-background-100 p-10 text-center">
          <i className="ri-inbox-line text-3xl text-foreground-500"></i>
          <p className="mt-2 text-foreground-950 font-semibold text-sm">Chưa có yêu cầu nào</p>
          <p className="mt-1 text-xs text-foreground-600">Khi khách gửi yêu cầu, chúng sẽ xuất hiện ở đây.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-3 items-start">
          <div className="lg:col-span-2 rounded-xl border border-background-200/60 bg-background-100 overflow-hidden">
            <div className="px-3 py-2 border-b border-background-200/60 bg-background-50">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-foreground-500">Danh sách ({requests.length})</p>
            </div>
            <div className="max-h-[520px] overflow-y-auto divide-y divide-background-200/40">
              {requests.map((r) => (
                <button
                  key={r.id}
                  onClick={() => setSelectedId(r.id)}
                  className={`w-full text-left px-3 py-2.5 transition-colors cursor-pointer ${selectedId === r.id ? 'bg-primary-500/10' : 'hover:bg-background-200/40'}`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-mono text-[11px] font-bold tracking-widest text-foreground-950">{r.request_code}</span>
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold whitespace-nowrap ${statusMeta[r.status]?.cls || 'bg-foreground-200/40 text-foreground-700'}`}>
                      {statusMeta[r.status]?.label || r.status}
                    </span>
                  </div>
                  <p className="mt-0.5 text-sm font-semibold text-foreground-900 line-clamp-1">{r.subject}</p>
                  <div className="mt-0.5 flex items-center gap-1.5 text-[11px] text-foreground-500">
                    <span className="truncate">{r.name}</span>
                    <span>•</span>
                    <span className="whitespace-nowrap">{formatDate(r.created_at)}</span>
                  </div>
                  {r.reply_count > 0 && <p className="mt-0.5 text-[11px] text-primary-400 font-semibold">{r.reply_count} phản hồi</p>}
                </button>
              ))}
            </div>
          </div>

          <div className="lg:col-span-3 rounded-xl border border-background-200/60 bg-background-100 overflow-hidden">
            {!selected ? (
              <div className="p-8 text-center text-sm text-foreground-600">Chọn một yêu cầu để xem chi tiết.</div>
            ) : (
              <div className="flex flex-col">
                <div className="px-4 py-3 border-b border-background-200/60">
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="min-w-0 flex-1">
                      <h2 className="text-sm font-bold text-foreground-950 line-clamp-2">{selected.subject}</h2>
                      <p className="text-[11px] text-foreground-500 mt-0.5">
                        <span className="font-semibold text-foreground-700">{selected.name}</span> • {formatDate(selected.created_at)}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <select
                        value={selected.status}
                        onChange={(e) => handleStatusChange(e.target.value)}
                        className={`px-2 py-1 rounded-full text-[10px] font-semibold border-none outline-none cursor-pointer ${statusMeta[selected.status]?.cls || 'bg-foreground-200/40 text-foreground-700'}`}
                      >
                        <option value="open">Mới</option>
                        <option value="answered">Đã trả lời</option>
                        <option value="resolved">Đã giải quyết</option>
                      </select>
                      <span className="font-mono text-[11px] font-bold tracking-widest text-foreground-950">{selected.request_code}</span>
                    </div>
                  </div>
                </div>

                <div className="px-4 py-3 border-b border-background-200/60 bg-background-50">
                  {editingMessage ? (
                    <div className="space-y-2">
                      <textarea
                        value={editMessageText}
                        onChange={(e) => setEditMessageText(e.target.value)}
                        rows={3}
                        className="w-full px-3 py-2 rounded-md bg-background-50 border border-background-300/60 text-sm text-foreground-900 focus:outline-none focus:border-primary-500 resize-none"
                      />
                      <div className="flex gap-2">
                        <button onClick={saveMessage} disabled={savingMessage} className="px-3 py-1.5 rounded-md bg-emerald-500 text-background-950 text-xs font-semibold cursor-pointer disabled:opacity-60 whitespace-nowrap">
                          {savingMessage ? 'Đang lưu...' : 'Lưu'}
                        </button>
                        <button onClick={() => setEditingMessage(false)} className="px-3 py-1.5 rounded-md bg-background-50 border border-background-300/60 text-xs font-semibold text-foreground-700 cursor-pointer whitespace-nowrap">Hủy</button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-start justify-between gap-2">
                      <p className="text-sm text-foreground-800 whitespace-pre-wrap flex-1">{selected.message}</p>
                      <button onClick={startEditMessage} className="shrink-0 px-2 py-1 rounded-md border border-background-300/60 text-[10px] font-semibold text-foreground-600 hover:text-primary-400 hover:border-primary-400/50 cursor-pointer whitespace-nowrap transition-colors">
                        <i className="ri-pencil-line mr-1"></i>Sửa
                      </button>
                    </div>
                  )}
                </div>

                <div ref={messagesContainerRef} className="flex-1 px-4 py-3 space-y-2 overflow-y-auto max-h-[320px]">
                  {replyLoading ? (
                    <p className="text-xs text-foreground-500">Đang tải phản hồi...</p>
                  ) : replies.length === 0 ? (
                    <p className="text-xs text-foreground-600">Chưa có phản hồi.</p>
                  ) : (
                    replies.map((r) => (
                      <div key={r.id} className={`rounded-lg p-2.5 border ${r.is_staff ? 'bg-primary-500/10 border-primary-500/20' : 'bg-background-50 border-background-200/60'}`}>
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold whitespace-nowrap ${r.is_staff ? 'bg-primary-500/20 text-primary-400' : 'bg-foreground-200/40 text-foreground-700'}`}>
                            {r.is_staff ? 'Hỗ trợ viên' : 'Khách'}
                          </span>
                          <span className="text-[10px] text-foreground-500">{formatDate(r.created_at)}</span>
                          {r.is_staff && editingReplyId !== r.id && (
                            <button onClick={() => startEditReply(r)} className="ml-auto px-2 py-0.5 rounded-md border border-background-300/60 text-[10px] font-semibold text-foreground-600 hover:text-primary-400 hover:border-primary-400/50 cursor-pointer whitespace-nowrap transition-colors">
                              <i className="ri-pencil-line mr-1"></i>Sửa
                            </button>
                          )}
                        </div>
                        {editingReplyId === r.id ? (
                          <div className="space-y-2">
                            <textarea
                              value={editReplyText}
                              onChange={(e) => setEditReplyText(e.target.value)}
                              rows={2}
                              className="w-full px-3 py-2 rounded-md bg-background-50 border border-background-300/60 text-sm text-foreground-900 focus:outline-none focus:border-primary-500 resize-none"
                            />
                            <div className="flex gap-2">
                              <button onClick={saveReply} disabled={savingReply} className="px-3 py-1.5 rounded-md bg-emerald-500 text-background-950 text-xs font-semibold cursor-pointer disabled:opacity-60 whitespace-nowrap">
                                {savingReply ? 'Đang lưu...' : 'Lưu'}
                              </button>
                              <button onClick={() => setEditingReplyId(null)} className="px-3 py-1.5 rounded-md bg-background-50 border border-background-300/60 text-xs font-semibold text-foreground-700 cursor-pointer whitespace-nowrap">Hủy</button>
                            </div>
                          </div>
                        ) : (
                          <>
                            {r.content && <p className="text-sm text-foreground-800 whitespace-pre-wrap">{r.content}</p>}
                            {r.attachment_url && (
                              isImageAttachment(r) ? (
                                <a href={r.attachment_url} target="_blank" rel="noreferrer noopener" className="mt-2 block">
                                  <img src={r.attachment_url} alt={r.attachment_name || 'Ảnh đính kèm'} className="max-w-full max-h-48 rounded-md border border-background-200/60 object-contain bg-background-50 cursor-pointer" />
                                </a>
                              ) : (
                                <a href={r.attachment_url} target="_blank" rel="noreferrer noopener" className="mt-2 inline-flex items-center gap-2 px-3 py-2 rounded-md bg-background-50 border border-background-200/60 text-xs font-semibold text-primary-400 hover:text-primary-300 cursor-pointer transition-colors">
                                  <i className="ri-file-download-line text-base"></i>
                                  <span className="truncate max-w-[180px]">{r.attachment_name || 'Tệp đính kèm'}</span>
                                </a>
                              )
                            )}
                          </>
                        )}
                      </div>
                    ))
                  )}
                  <div ref={messagesEndRef} />
                </div>

                <form onSubmit={handleReply} className="px-4 py-3 border-t border-background-200/60">
                  <textarea
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                    maxLength={2000}
                    rows={2}
                    placeholder="Viết câu trả lời cho khách..."
                    className="w-full px-3 py-2 rounded-md bg-background-50 border border-background-300/60 text-sm text-foreground-900 placeholder:text-foreground-500 focus:outline-none focus:border-primary-500 resize-none"
                  />

                  {attachment && (
                    <div className="mt-2 flex items-center gap-2 rounded-md bg-background-50 border border-background-200/60 p-2">
                      {attachmentPreview ? (
                        <img src={attachmentPreview} alt="Xem trước" className="w-12 h-12 rounded-md object-cover border border-background-200/60" />
                      ) : (
                        <div className="w-12 h-12 rounded-md bg-secondary-100 text-secondary-900 flex items-center justify-center">
                          <i className="ri-file-line text-xl"></i>
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-semibold text-foreground-900 truncate">{attachment.name}</p>
                        <p className="text-[10px] text-foreground-500">{(attachment.size / 1024).toFixed(0)} KB</p>
                      </div>
                      <button type="button" onClick={clearAttachment} className="w-7 h-7 flex items-center justify-center rounded-full text-foreground-500 hover:text-rose-400 hover:bg-rose-500/10 cursor-pointer transition-colors">
                        <i className="ri-close-line"></i>
                      </button>
                    </div>
                  )}

                  <input ref={fileInputRef} type="file" accept="image/*,.pdf,.txt,.zip,.doc,.docx,.xls,.xlsx" className="hidden" onChange={(e) => handleFileChange(e.target.files?.[0] || null)} />

                  <div className="mt-2 flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2">
                      <button type="button" onClick={() => fileInputRef.current?.click()} className="w-8 h-8 flex items-center justify-center rounded-md border border-background-300/60 text-foreground-600 hover:text-primary-400 hover:border-primary-400/50 cursor-pointer transition-colors" title="Đính kèm hình ảnh / file">
                        <i className="ri-attachment-2 text-base"></i>
                      </button>
                      <p className="text-xs text-foreground-500">{replyText.length}/2000</p>
                    </div>
                    <button type="submit" disabled={sending || (!replyText.trim() && !attachment)} className="px-4 py-2 rounded-md bg-primary-500 text-background-950 font-semibold text-xs hover:bg-primary-600 disabled:opacity-60 whitespace-nowrap cursor-pointer transition-colors">
                      {uploading ? 'Đang tải lên...' : sending ? 'Đang gửi...' : 'Gửi trả lời'}
                    </button>
                  </div>
                </form>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}